import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';

function SignUp({ theme, showNotification, setIsDemo }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    withCredentials: true, // si tu as des cookies/auth
  });

   // Interceptor : ajoute le token à chaque requête
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token'); // ou ton state/context
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/register', { email, password });
      showNotification('Compte créé avec succès ! Vous pouvez maintenant vous connecter.', 'success');
      setIsDemo(false);
      localStorage.setItem('token', response.data.access_token); // ou ton setToken
      navigate('/dashboard/onboarding');
    } catch (error) {
      console.error('Error details:', error.response);
      showNotification('Erreur lors de la création du compte : ' + (error.response?.data?.detail || 'Vérifiez vos informations'), 'error');
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await api.post('/api/auth/google', {
        credential: credentialResponse.credential
      });
      showNotification('Inscription avec Google réussie !', 'success');
      localStorage.setItem('token', res.data.access_token);
      setIsDemo(false);
      navigate('/dashboard/onboarding');
    } catch (err) {
      showNotification('Erreur avec Google : ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleGoogleError = () => {
    showNotification('Échec de la connexion Google', 'error');
  };

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'} flex items-center justify-center p-6`}>
      <div className={`w-full max-w-md p-8 rounded-2xl shadow-2xl ${theme === 'light' ? 'bg-white' : 'bg-gray-800'} border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
        <h2 className={`text-3xl font-bold text-center mb-8 ${theme === 'light' ? 'text-gray-900' : 'text-gray-100'}`}>
          Créer un compte
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${theme === 'light' ? 'border-gray-300 bg-white focus:border-blue-500' : 'border-gray-600 bg-gray-700 focus:border-blue-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition text-sm`}
              required
              placeholder="vous@exemple.com"
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${theme === 'light' ? 'border-gray-300 bg-white focus:border-blue-500' : 'border-gray-600 bg-gray-700 focus:border-blue-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition text-sm`}
              required
              placeholder="Au moins 8 caractères"
              minLength="8"
            />
          </div>

          <button
            type="submit"
            className="w-full py-3.5 px-6 text-lg font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg hover:from-emerald-600 hover:to-green-700 transform hover:scale-[1.02] active:scale-100 transition-all duration-200 shadow-xl"
          >
            Créer mon compte
          </button>
        </form>

        <p className={`text-center mt-8 text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
          Déjà un compte ?{' '}
          <a href="/login" className="font-semibold text-emerald-600 dark:text-emerald-400 hover:underline transition-colors">
            Se connecter
          </a>
        </p>

        {/* Séparateur "Ou" */}
        <div className="relative my-10">
          <div className="absolute inset-0 flex items-center">
            <div className={`w-full border-t ${theme === 'light' ? 'border-gray-300' : 'border-gray-600'}`} />
          </div>
          <div className="relative flex justify-center">
            <span className={`px-4 py-2 text-sm font-medium rounded-full ${theme === 'light' ? 'bg-white text-gray-500 shadow-sm' : 'bg-gray-800 text-gray-400 border border-gray-600'}`}>
              Ou continuez avec
            </span>
          </div>
        </div>

        {/* Bouton Google parfaitement intégré */}
        <div className="w-full">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme={theme === 'dark' ? 'filled_black' : 'outline'}
            size="large"
            text="signup_with"
            shape="rectangular"
            width="100%"
            logo_alignment="center"
            auto_select={false}
          />
        </div>

        <p className={`text-center mt-6 text-xs ${theme === 'light' ? 'text-gray-500' : 'text-gray-500'}`}>
          En vous inscrivant, vous acceptez nos{' '}
          <a href="/conditions" className="underline hover:text-blue-600">Conditions d'utilisation</a>{' '}
          et notre{' '}
          <button
            onClick={() => navigate('/politique-confidentialite')}
            className="px-1 underline hover:text-blue-600 dark:hover:text-blue-400 transition"
          >
            <a> Politique de confidentialité</a>
          </button>
        </p>
      </div>
    </div>
  );
}

export default SignUp;